/**
 * Chat Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Chat, Message, ChatData, MessageData, ChatType, ChatStatus, MessageType } from '../models/Chat.model';

export class ChatService {
  private firestore: FirestoreService;
  private readonly CHATS_COLLECTION = 'chats';
  private readonly MESSAGES_COLLECTION = 'messages';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createChat(chatData: Partial<ChatData>): Promise<Chat> {
    const chat = new Chat(chatData);
    const docRef = await this.firestore.addDocument(this.CHATS_COLLECTION, chat.toFirestore());
    chat.id = docRef.id;
    return chat;
  }

  async getChat(id: string): Promise<Chat | null> {
    const doc = await this.firestore.getDocument(this.CHATS_COLLECTION, id);
    return doc ? Chat.fromFirestore({ id, ...doc }) : null;
  }

  async updateChat(id: string, updates: Partial<ChatData>): Promise<void> {
    await this.firestore.updateDocument(this.CHATS_COLLECTION, id, {
      ...updates,
      updatedAt: new Date()
    });
  }

  async deleteChat(id: string): Promise<void> {
    const messages = await this.getChatMessages(id);
    const deletePromises = messages.map(message => this.deleteMessage(message.id!));
    await Promise.all(deletePromises);
    await this.firestore.deleteDocument(this.CHATS_COLLECTION, id);
  }

  async getAllChats(limit?: number): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [],
      { field: 'lastMessageAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async getUserChats(userId: string): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'lastMessageAt', direction: 'desc' }
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async getChatsByStatus(status: ChatStatus): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [{ field: 'status', operator: '==', value: status }],
      { field: 'lastMessageAt', direction: 'desc' }
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async getChatsByType(type: ChatType): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [{ field: 'type', operator: '==', value: type }],
      { field: 'lastMessageAt', direction: 'desc' }
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async getAssignedChats(adminId: string): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [{ field: 'adminId', operator: '==', value: adminId }],
      { field: 'lastMessageAt', direction: 'desc' }
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async getUnassignedChats(): Promise<Chat[]> {
    const docs = await this.firestore.queryDocuments(
      this.CHATS_COLLECTION,
      [
        { field: 'adminId', operator: '==', value: null },
        { field: 'status', operator: 'in', value: ['open', 'in_progress'] }
      ],
      { field: 'priority', direction: 'desc' }
    );
    return docs.map(doc => Chat.fromFirestore(doc));
  }

  async assignChatToAdmin(chatId: string, adminId: string): Promise<void> {
    const chat = await this.getChat(chatId);
    if (!chat) throw new Error('Chat not found');

    chat.assignToAdmin(adminId);
    await this.updateChat(chatId, chat.toFirestore());
  }

  async updateChatStatus(chatId: string, status: ChatStatus): Promise<void> {
    const chat = await this.getChat(chatId);
    if (!chat) throw new Error('Chat not found');

    chat.updateStatus(status);
    await this.updateChat(chatId, chat.toFirestore());
  }

  async createMessage(messageData: Partial<MessageData>): Promise<Message> {
    const message = new Message(messageData);

    // Store messages in subcollection: chats/{chatId}/messages (same as Landing)
    if (!message.chatId) {
      throw new Error('chatId is required to create a message');
    }

    const messagesPath = `chats/${message.chatId}/messages`;
    const docRef = await this.firestore.addDocument(messagesPath, message.toFirestore());
    message.id = docRef.id;

    // Update chat with last message info
    await this.updateChatWithNewMessage(message.chatId, message.content);

    return message;
  }

  async getMessage(id: string): Promise<Message | null> {
    const doc = await this.firestore.getDocument(this.MESSAGES_COLLECTION, id);
    return doc ? Message.fromFirestore({ id, ...doc }) : null;
  }

  async updateMessage(id: string, updates: Partial<MessageData>): Promise<void> {
    await this.firestore.updateDocument(this.MESSAGES_COLLECTION, id, updates);
  }

  async deleteMessage(id: string): Promise<void> {
    await this.firestore.deleteDocument(this.MESSAGES_COLLECTION, id);
  }

  async getChatMessages(chatId: string, limit?: number): Promise<Message[]> {
    // Read from subcollection: chats/{chatId}/messages (same as Landing)
    const messagesPath = `chats/${chatId}/messages`;
    const docs = await this.firestore.queryDocuments(
      messagesPath,
      [], // no filters needed - already scoped to chatId by path
      { field: 'createdAt', direction: 'asc' },
      limit
    );
    return docs.map(doc => Message.fromFirestore(doc));
  }

  async markMessageAsRead(messageId: string, userId: string): Promise<void> {
    const message = await this.getMessage(messageId);
    if (message) {
      message.markAsRead(userId);
      await this.updateMessage(messageId, message.toFirestore());
    }
  }

  async markAllChatMessagesAsRead(chatId: string, userId: string): Promise<void> {
    const messages = await this.getChatMessages(chatId);
    const updatePromises = messages
      .filter(message => !message.isReadBy(userId))
      .map(message => this.markMessageAsRead(message.id!, userId));

    await Promise.all(updatePromises);

    const chat = await this.getChat(chatId);
    if (chat) {
      chat.resetUnreadCount();
      await this.updateChat(chatId, chat.toFirestore());
    }
  }

  async sendMessage(
    chatId: string,
    senderId: string,
    content: string,
    type: MessageType = 'text',
    senderType: 'user' | 'admin' | 'system' = 'user',
    attachments?: string[]
  ): Promise<Message> {
    return this.createMessage({
      chatId,
      senderId,
      senderType,
      type,
      content,
      attachments
    });
  }

  async sendSystemMessage(chatId: string, content: string): Promise<Message> {
    return this.sendMessage(chatId, 'system', content, 'text', 'system');
  }

  async createBookingChat(userId: string, bookingId: string, subject?: string): Promise<Chat> {
    return this.createChat({
      userId,
      bookingId,
      type: 'booking_related',
      subject: subject || `Booking #${bookingId}`,
      priority: 'medium'
    });
  }

  async createSupportChat(userId: string, subject: string, priority: 'low' | 'medium' | 'high' = 'medium'): Promise<Chat> {
    return this.createChat({
      userId,
      type: 'customer_support',
      subject,
      priority
    });
  }

  private async updateChatWithNewMessage(chatId: string, content: string): Promise<void> {
    const chat = await this.getChat(chatId);
    if (chat) {
      chat.updateLastMessage(content);
      chat.incrementUnreadCount();
      await this.updateChat(chatId, chat.toFirestore());
    }
  }

  async getChatStats(): Promise<{
    totalChats: number;
    openChats: number;
    inProgressChats: number;
    resolvedChats: number;
    closedChats: number;
    unassignedChats: number;
    averageResponseTime?: number;
  }> {
    const chats = await this.getAllChats();

    const stats = chats.reduce((acc, chat) => {
      acc.totalChats++;
      acc[`${chat.status}Chats`]++;

      if (!chat.adminId && (chat.status === 'open' || chat.status === 'in_progress')) {
        acc.unassignedChats++;
      }

      return acc;
    }, {
      totalChats: 0,
      openChats: 0,
      inProgressChats: 0,
      resolvedChats: 0,
      closedChats: 0,
      unassignedChats: 0
    });

    return stats;
  }

  subscribeToChats(callback: (chats: Chat[]) => void): () => void {
    return this.firestore.subscribeToQuery(
      this.CHATS_COLLECTION,
      [],
      { field: 'lastMessageAt', direction: 'desc' },
      undefined,
      (docs) => {
        const chats = docs.map(doc => Chat.fromFirestore(doc));
        callback(chats);
      }
    );
  }

  subscribeToUserChats(userId: string, callback: (chats: Chat[]) => void): () => void {
    return this.firestore.subscribeToQuery(
      this.CHATS_COLLECTION,
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'lastMessageAt', direction: 'desc' },
      undefined,
      (docs) => {
        const chats = docs.map(doc => Chat.fromFirestore(doc));
        callback(chats);
      }
    );
  }

  subscribeToChatMessages(chatId: string, callback: (messages: Message[]) => void): () => void {
    // Subscribe to subcollection: chats/{chatId}/messages (same as Landing)
    const messagesPath = `chats/${chatId}/messages`;
    return this.firestore.subscribeToQuery(
      messagesPath,
      [], // no filters needed - already scoped to chatId by path
      { field: 'createdAt', direction: 'asc' },
      50, // limit to 50 recent messages
      (docs) => {
        const messages = docs.map(doc => Message.fromFirestore(doc));
        callback(messages);
      }
    );
  }

  subscribeToUnassignedChats(callback: (chats: Chat[]) => void): () => void {
    return this.firestore.subscribeToQuery(
      this.CHATS_COLLECTION,
      [
        { field: 'adminId', operator: '==', value: null },
        { field: 'status', operator: 'in', value: ['open', 'in_progress'] }
      ],
      { field: 'priority', direction: 'desc' },
      undefined,
      (docs) => {
        const chats = docs.map(doc => Chat.fromFirestore(doc));
        callback(chats);
      }
    );
  }
}